import {Box3, Group, Mesh, MathUtils, Vector3, type Object3D} from 'three'

import type {ModelConfig} from '../content/manifest.types'

/**
 * The GLB-variability firewall.
 *
 * A swapped-in model may be authored in centimetres, have a pivot at its
 * centre, or be rotated 90 degrees. Everything that adapts an arbitrary GLB to
 * "1 unit == 1 metre, feet at the origin, facing -Z" happens here, so the rest
 * of the app can assume that contract.
 *
 * Transform ownership (see docs) - two owners, no overlap:
 *
 *   root    <- PlacementController: world position, placement yaw, user scale
 *    └ pivot <- this module: normalisation scale, y offset, authored yaw
 *       └ content = gltf.scene: recentre on XZ, drop feet to y = 0
 */

/** Guards against a corrupt or mis-authored file producing an absurd scale. */
const MIN_SCALE = 1e-4
const MAX_SCALE = 1e4

export type ModelWarning =
  | {readonly kind: 'noMeshes'}
  | {readonly kind: 'degenerateBounds'; readonly size: readonly [number, number, number]}
  | {readonly kind: 'scaleClamped'; readonly requested: number; readonly applied: number}

export interface NormalizedModel {
  /** Hand this to the PlacementController. */
  readonly pivot: Group
  /** Height in the file's own units, before scaling. */
  readonly measuredHeight: number
  /** Footprint radius after scaling - useful for sizing the shadow catcher. */
  readonly groundRadius: number
  readonly appliedScale: number
  /**
   * Bind-pose size in the file's own units, before `appliedScale`.
   *
   * `measuredHeight` is this tuple's y; the other two axes are here because the
   * tap-target proxy in src/interaction/model-hit.ts needs a volume, and taking
   * a fresh Box3 downstream would have to run AFTER the mixer exists - the one
   * ordering step 2 below forbids. Same tuple shape as
   * `ModelWarning.degenerateBounds`.
   */
  readonly localSize: readonly [number, number, number]
  readonly warnings: readonly ModelWarning[]
}

export const describeWarning = (warning: ModelWarning): string => {
  switch (warning.kind) {
    case 'noMeshes':
      return 'GLB contains no meshes'
    case 'degenerateBounds':
      return `GLB bounds are degenerate (${warning.size.map((v) => v.toFixed(4)).join(' x ')}); skipped normalisation`
    case 'scaleClamped':
      return `scale ${warning.requested.toExponential(2)} clamped to ${warning.applied.toExponential(2)}`
  }
}

export const normalizeModel = (content: Object3D, config: ModelConfig): NormalizedModel => {
  const warnings: ModelWarning[] = []

  // --- 1. Prepare the meshes ------------------------------------------------
  let meshCount = 0
  content.traverse((object) => {
    if (!(object instanceof Mesh)) return
    meshCount++
    object.castShadow = config.castShadow
    // No receiveShadow: self-shadowing costs more than it adds on a single
    // stylised hero model, and the contact shadow comes from a dedicated plane.

    // three.js computes a SkinnedMesh's bounding sphere once, from whatever
    // pose it is in, and never invalidates it. An animation that moves a limb
    // outside that stale sphere gets the whole mesh culled - "the tail
    // disappeared". For one hero model, per-object culling saves 24 sphere
    // tests, which is not worth that failure mode.
    object.frustumCulled = false
  })
  if (meshCount === 0) warnings.push({kind: 'noMeshes'})

  // --- 2. Measure -----------------------------------------------------------
  // Box3.expandByObject only calls updateWorldMatrix(false, false) - it does
  // NOT walk up to ancestors. Refresh the whole subtree first so the measured
  // box reflects the file's own node transforms.
  content.updateMatrixWorld(true)

  // For a SkinnedMesh, Box3 uses SkinnedMesh.boundingBox, which three.js
  // computes by skinning every vertex in the CURRENT pose and then caches
  // forever. This must therefore run BEFORE the AnimationMixer is created and
  // stepped, so we measure the bind pose rather than an arbitrary frame.
  const box = new Box3().setFromObject(content)
  const size = box.getSize(new Vector3())
  const center = box.getCenter(new Vector3())
  const measuredHeight = size.y

  // --- 3. Scale -------------------------------------------------------------
  let appliedScale = 1
  const degenerate = !Number.isFinite(measuredHeight) || measuredHeight <= 0
  if (degenerate) {
    warnings.push({kind: 'degenerateBounds', size: [size.x, size.y, size.z]})
  } else if (config.targetHeightMeters !== null) {
    const requested = config.targetHeightMeters / measuredHeight
    appliedScale = MathUtils.clamp(requested, MIN_SCALE, MAX_SCALE)
    if (appliedScale !== requested) {
      warnings.push({kind: 'scaleClamped', requested, applied: appliedScale})
    }
  }

  // --- 4. Assemble ----------------------------------------------------------
  // The alignment offset lives on its own node rather than being written into
  // `content.position`. Mutating the loaded object would only be correct while
  // its own transform is identity, which is true for a GLTFLoader scene root
  // today but is exactly the assumption a swapped-in file could break.
  const align = new Group()
  align.name = 'model-align'
  if (!degenerate) {
    // Recentre on XZ and drop the LOWEST point (not the centre) to y = 0, so
    // the model's feet land on the placement point rather than its waist.
    align.position.set(-center.x, -box.min.y, -center.z)
  }
  align.add(content)

  const pivot = new Group()
  pivot.name = 'model-pivot'
  pivot.scale.setScalar(appliedScale)
  // In metres: `pivot.position` is expressed in the parent's (unscaled) space,
  // so this must NOT be divided by appliedScale.
  pivot.position.y = config.yOffsetMeters
  pivot.rotation.y = MathUtils.degToRad(config.yawDegrees)
  pivot.add(align)

  const groundRadius = Math.max(size.x, size.z) * 0.5 * appliedScale

  return {
    pivot,
    measuredHeight,
    groundRadius,
    appliedScale,
    localSize: [size.x, size.y, size.z],
    warnings,
  }
}
